import { Drawer, Tag } from 'antd';
import type { UserData } from '../CampaignReport.type';
import { Icon } from 'Components';
import { INTERACTION_COLOR, RESULT_COLOR, STATUS_COLOR } from '../CampaignReport.constant';
import { formatDateTime, joinItems } from 'PhishingManager/utils';
import { useTranslation } from 'react-i18next';

const UserDetailDrawer = ({ data, visible, onClose }: {
  data: UserData | null,
  visible: boolean,
  onClose: () => void
}) => {
  const { t } = useTranslation();
  const contentPath = 'phishingManager.report.table.userDrawer.sections';

  const { color: resultColor, bg: resultBg } = RESULT_COLOR[data?.result || ''] || RESULT_COLOR.Failed;
  const { color: interactionColor, bg: interactionBg } = INTERACTION_COLOR;
  const name = `${data?.first_name || ""} ${data?.last_name || ""}`.trim();
  const confidenceTag = (data?.confidence || 'low')
  const confidenceColor = STATUS_COLOR[confidenceTag];
  const emailDeliveryTag = (data?.email_delivery || 'No');
  const emailDeliveryColor = STATUS_COLOR[emailDeliveryTag === "Yes" ? 'high' : 'low'];
  const ipAddress = `${data?.ip || ""} ${data?.city || ""}  ${data?.state || ""}  ${data?.country || ""}`.trim();
  const submissionDate = data?.event_date ? formatDateTime(data.event_date, "M/D/YYYY h:mm A") : '-';
  const isNaTag = data?.source === "N/A";
  const sourceTag = data?.source === "System" ? t(`${contentPath}.automationAnalysis.detected`) : t('phishingManager.report.table.filters.performedBy.human');
  const sourceIcon = data?.source === "System" ? "systemInteraction" : data?.source === "Human" ? "human" : "dashIcon";

  return (
    <Drawer
      title={
        <div className='header-section'>
          <div className='title-content'>
            <span className='title'>{name}</span>
            <Icon type='close' onClick={onClose} />
          </div>
          <Tag className='status-tag' style={{ color: resultColor, background: resultBg }}>
            {data?.result || ''}
          </Tag>
        </div>
      }
      className='user-data-drawer'
      placement="right"
      visible={visible}
      onClose={onClose}
      width={450}
      height="100vh">
      <div className='drawer-body'>
        <div className='data-section'>
          <span className='title'>{t(`${contentPath}.details.title`)}</span>
          <span className='description'>
            {t(`${contentPath}.details.property`)}: {joinItems(data?.properties || '', ", ")}
          </span>
          <span className='description'>
            {t(`${contentPath}.details.group`)}: {joinItems(data?.groups || '', ", ")}
          </span>
          <span className='description'>
            {t(`${contentPath}.details.email`)}: {data?.email || ''}
          </span>
        </div>

        <div className='data-section'>
          <span className='title'>{t(`${contentPath}.automationAnalysis.title`)}</span>

          <span className='description' style={{ textTransform: "capitalize" }}>
            {sourceTag} {t(`${contentPath}.automationAnalysis.interaction`)} 
            <Icon type={sourceIcon} style={{ background: "#E8F1FB" }} />
          </span>

          <span className='description'>
            {t(`${contentPath}.automationAnalysis.confidence`)}: 
            <Tag className='status-tag' style={{ color: isNaTag ? interactionColor : confidenceColor.color, background: isNaTag ? interactionBg : confidenceColor.bg }}>
              {isNaTag ? "N/A" : confidenceTag}
            </Tag>
          </span>
          <span className='info-banner'>{data?.key_factors ?? '-'}</span>
        </div>

        <div className='data-section'>
          <span className='title'>{t(`${contentPath}.technicalDetails.title`)}</span>
          <span className='description'>{ipAddress}</span>
          <span className='description'>
            {t(`${contentPath}.technicalDetails.isp`)}: {data?.isp || '-'}
          </span>
          <span className='description'>
            {t(`${contentPath}.technicalDetails.userAgent`)}:
          </span>
          <span className='info-banner'>{data?.user_agent || '-'}</span>
        </div>

        <div className='data-section'>
          <span className='title'>{t(`${contentPath}.deliveryInteraction.title`)}</span>

          <span className='description'>
            {t(`${contentPath}.deliveryInteraction.emailDelivered`)}: 
            <span style={{ textTransform: "capitalize" }}>{emailDeliveryTag}</span>
            <Icon
              type={emailDeliveryTag === "No" ? 'close' : 'check'}
              style={{ color: emailDeliveryColor.color, background: emailDeliveryColor.bg }}
            />
          </span>
          <span className='description'>
            {t(`${contentPath}.deliveryInteraction.interaction`)}: 
            <Tag className='status-tag' style={{ color: interactionColor, background: interactionBg }}>
              {data?.interaction || '-'}
            </Tag>
          </span>
          <span className='description'>
            {t(`${contentPath}.deliveryInteraction.submission`)}: {submissionDate}
          </span>
        </div>
      </div>
    </Drawer>
  );
};

export default UserDetailDrawer;